import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, getHotArticles, getLatestArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function Home() {
  const [subStatus, setSubStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [subError, setSubError] = useState('');

  const hotArticles = getHotArticles();
  const latestArticles = getLatestArticles(8);
  const heroArticle = hotArticles[0] || latestArticles[0];
  const trendingList = articles
    .sort((a, b) => b.views - a.views)
    .slice(0, 6);

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setSubStatus('success');
      return;
    }
    setSubStatus('submitting');
    setSubError('');
    try {
      const response = await fetch('https://readdy.ai/api/form/d9q35asnlsngrm4llqo0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      if (response.ok && parsed.code === 'OK') {
        setSubStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setSubError('订阅失败，请稍后再试');
        } else {
          setSubError(serverMsg || '订阅失败，请稍后再试');
        }
        setSubStatus('error');
      }
    } catch {
      setSubError('网络错误，请检查连接后重试');
      setSubStatus('error');
    }
  };

  const homepageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '爆料吃瓜',
    url: 'https://bagua.example.com/',
    description: '爆料吃瓜平台汇聚全网最新、最热、最劲爆的娱乐八卦与社会热点，第一时间带你围观真实瓜料，海量爆料持续更新，吃瓜不断档！',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://bagua.example.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '爆料吃瓜',
    url: 'https://bagua.example.com/',
    description: '爆料吃瓜平台汇聚全网最新、最热、最劲爆的娱乐八卦与社会热点',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-6666',
      contactType: 'customer service',
      email: 'tips@bagua-news.com',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(homepageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="w-full px-4 md:px-6 pt-6 pb-10">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                {heroArticle && (
                  <ArticleCard article={heroArticle} variant="hero" />
                )}
              </div>

              {/* Trending Sidebar */}
              <div className="bg-background-50 border border-background-200/60 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-sm">
                    <i className="ri-fire-line"></i>
                  </span>
                  <h3 className="text-base font-semibold text-foreground-950">热搜榜</h3>
                </div>
                <ol className="space-y-0">
                  {trendingList.map((article, index) => (
                    <li key={article.id}>
                      <Link
                        to={`/article/${article.id}`}
                        className="flex items-start gap-3 py-3 border-b border-background-200/40 last:border-b-0 group cursor-pointer"
                      >
                        <span
                          className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-md text-xs font-bold ${
                            index < 3
                              ? 'bg-primary-500 text-background-50'
                              : 'bg-background-200 text-foreground-600'
                          }`}
                        >
                          {index + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground-800 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors">
                            {article.title}
                          </p>
                          <span className="text-xs text-foreground-500 mt-1 inline-block">
                            {formatViews(article.views)} 热度
                          </span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </section>

          {/* Category Navigation */}
          <section className="w-full px-4 md:px-6 pb-8">
            <div className="flex items-center gap-3 overflow-x-auto pb-1 flex-wrap">
              <Link
                to="/"
                className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-primary-500 text-background-50 whitespace-nowrap"
              >
                <i className="ri-home-line"></i> 全部
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-500 transition-colors whitespace-nowrap"
                >
                  <i className={cat.icon}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </section>

          {/* Hot Articles Grid */}
          {hotArticles.length > 1 && (
            <section className="w-full px-4 md:px-6 pb-12">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
                  <i className="ri-fire-line"></i>
                </span>
                <h2 className="text-xl font-bold text-foreground-950">热门爆料</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {hotArticles.slice(1, 5).map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </section>
          )}

          {/* Latest Articles Grid */}
          <section className="w-full px-4 md:px-6 pb-16">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-500 text-background-50">
                <i className="ri-timer-line"></i>
              </span>
              <h2 className="text-xl font-bold text-foreground-950">最新瓜料</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {latestArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </section>

          {/* Newsletter Section */}
          <section className="w-full px-4 md:px-6 pb-16">
            <div className="bg-background-100 rounded-2xl p-8 md:p-12 text-center max-w-3xl mx-auto">
              <span className="inline-flex w-14 h-14 items-center justify-center rounded-2xl bg-primary-100 text-primary-500 mb-5">
                <i className="ri-mail-send-line text-2xl"></i>
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                不想错过任何瓜？
              </h2>
              <p className="text-foreground-600 mb-6 max-w-lg mx-auto">
                订阅我们的每日瓜报，最新爆料第一时间送到你手上，吃瓜永远快人一步！
              </p>
              <form
                className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
                data-readdy-form="true"
                action="https://readdy.ai/api/form/d9q35asnlsngrm4llqo0"
                method="POST"
                onSubmit={handleSubscribe}
              >
                <input
                  type="email"
                  name="email"
                  placeholder="输入你的邮箱地址"
                  required
                  className="flex-1 px-4 py-3 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                />
                <button
                  type="submit"
                  disabled={subStatus === 'submitting'}
                  className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
                >
                  {subStatus === 'submitting' ? '订阅中...' : '立即订阅'}
                </button>
                <div className="honeypot-field">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden={true} readOnly={true} />
                </div>
              </form>
              {subStatus === 'success' && (
                <p className="mt-4 text-sm text-green-600 flex items-center justify-center gap-1">
                  <i className="ri-checkbox-circle-line"></i> 订阅成功！瓜料第一时间送到你手上
                </p>
              )}
              {subStatus === 'error' && (
                <p className="mt-4 text-sm text-red-600 flex items-center justify-center gap-1">
                  <i className="ri-error-warning-line"></i> {subError}
                </p>
              )}
            </div>
          </section>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}